import z from "zod";

import { ModelSchema } from "@demo/shared/types";

export const RECOVERY_MAX_IMAGE_BYTES = 3 * 1024 * 1024;
export const RECOVERY_MAX_BODY_BYTES = RECOVERY_MAX_IMAGE_BYTES + 64 * 1024;
export const RECOVERY_PROMPT_VERSION = "same-object-v2";

const coordinate = z.number().finite().min(0).max(1000);
const point = z.object({ x: coordinate, y: coordinate }).strict();
export const RecoveryBoxSchema = z
  .object({ top_left: point, bottom_right: point })
  .strict()
  .refine((box) => box.top_left.x < box.bottom_right.x && box.top_left.y < box.bottom_right.y);

const timestamp = z.number().finite().nonnegative();
const image = z
  .object({
    timestamp,
    duration: z.number().finite().positive().max(10),
    width: z.number().int().positive().max(1536),
    height: z.number().int().positive().max(1536),
    sourceWidth: z.number().int().positive().max(32768),
    sourceHeight: z.number().int().positive().max(32768),
    dataUrl: z
      .string()
      .max(RECOVERY_MAX_IMAGE_BYTES)
      .regex(/^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/),
  })
  .strict();
const reference = z
  .object({
    anchor: z.object({ id: z.string().min(1).max(256), time: timestamp, coordinates: RecoveryBoxSchema }).strict(),
    image,
  })
  .strict();

export const RecoverTrackRequestSchema = z
  .object({
    model: ModelSchema,
    sessionUuid: z.string().uuid(),
    parentRunUuid: z.string().uuid(),
    // Deliberately opaque: media URLs and signed query parameters never enter recovery logs.
    sourceId: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9_-]+$/),
    trackId: z.string().min(1).max(256),
    trackLabel: z.string().max(256).optional(),
    target: image,
    references: z.tuple([reference, reference]),
  })
  .strict()
  .superRefine((request, ctx) => {
    const [start, end] = request.references;
    const images = [start.image, end.image, request.target];
    if (images.reduce((bytes, image) => bytes + image.dataUrl.length, 0) > RECOVERY_MAX_IMAGE_BYTES) {
      ctx.addIssue({ code: "custom", message: "Recovery images exceed the encoded size limit" });
    }
    if (
      end.anchor.time - start.anchor.time > 1 + 1e-6 ||
      request.target.timestamp <= start.anchor.time ||
      request.target.timestamp >= end.anchor.time
    ) {
      ctx.addIssue({ code: "custom", message: "Recovery target must lie inside an eligible anchored interval" });
    }
    for (const reference of request.references) {
      if (
        reference.anchor.time < reference.image.timestamp - 1e-6 ||
        reference.anchor.time >= reference.image.timestamp + reference.image.duration + 1e-6
      ) {
        ctx.addIssue({ code: "custom", message: "Reference frame does not contain its anchor" });
      }
    }
    for (const image of images) {
      if (
        image.sourceWidth !== request.target.sourceWidth ||
        image.sourceHeight !== request.target.sourceHeight ||
        Math.abs(image.width * image.sourceHeight - image.height * image.sourceWidth) >
          Math.max(image.sourceWidth, image.sourceHeight)
      ) {
        ctx.addIssue({ code: "custom", message: "Recovery images must preserve the source aspect ratio" });
      }
    }
  });

export type RecoverTrackRequest = z.infer<typeof RecoverTrackRequestSchema>;

const responseMetadata = {
  requestId: z.string().uuid(),
  usage: z
    .object({
      promptTokens: z.number().int().nonnegative(),
      completionTokens: z.number().int().nonnegative(),
      totalTokens: z.number().int().nonnegative(),
    })
    .optional(),
  timings: z.object({ totalMs: z.number().finite().nonnegative() }),
};
export const RecoverTrackResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("found"), box: RecoveryBoxSchema, ...responseMetadata }),
  z.object({ status: z.literal("no_match"), ...responseMetadata }),
]);
export type RecoverTrackResponse = z.infer<typeof RecoverTrackResponseSchema>;
